/*
 * The attachments of one message, as the timeline shows them (spec 07 §4 "Timeline").
 *
 * Pictures first, then everything else in the order the sender put them in. A picture is drawn from
 * its thumbnail inside a box reserved from the stored width and height, so the timeline does not
 * jump when the image arrives — an attachment that made the whole scrollback move would be worse
 * than one that took a moment to appear.
 *
 * A file that is not a picture is one row, drawn by `AttachmentRow` — the same row the conversation's
 * file list uses, so the two surfaces can never disagree about what a file offers.
 *
 * What counts as a picture is deliberately narrow: an image the server could decode, which is
 * exactly the set it has pixel dimensions and a thumbnail for. An SVG is an image by MIME type and
 * a document by behaviour, so it falls through to that row and is only ever downloaded — it is
 * never rendered inline and never navigated to (security.md, spec 07 §5).
 */

import { useCallback, useState } from "react"
import type { AttachmentWire } from "../../../core/wire/index.ts"
import { t } from "../i18n/t.ts"
import { AttachmentRow } from "./AttachmentRow.tsx"
import "./attachments.css"
import { Lightbox } from "./Lightbox.tsx"

/** The alt text of an image: the caption somebody wrote, else the file's own name. Never empty. */
export function altOf(file: AttachmentWire): string {
	return file.caption !== "" ? file.caption : file.filename
}

/** True for an attachment the server decoded into pixels — the only thing drawn as a picture. */
export function isPicture(file: AttachmentWire): boolean {
	return (
		file.kind === "image" &&
		file.width !== undefined &&
		file.height !== undefined &&
		file.width > 0 &&
		file.height > 0
	)
}

export interface AttachmentListProps {
	attachments: readonly AttachmentWire[]
}

export function AttachmentList({ attachments }: AttachmentListProps) {
	const [openAt, setOpenAt] = useState<number | null>(null)
	const close = useCallback(() => setOpenAt(null), [])
	/*
	 * The pictures whose bytes never came. A thumbnail can be refused after the message has arrived —
	 * a rate limit on the file route under load, a revoked session, a byte range the store lost — and
	 * what the browser draws then is its own broken-image glyph with the file name beside it, which is
	 * the one place in the product where a failure is reported in the browser's words rather than the
	 * product's (N5-R1-13). A picture that cannot be drawn is a file: the row below says what it is
	 * called, how big it is and offers the download, which is everything still true about it.
	 */
	const [broken, setBroken] = useState<ReadonlySet<number>>(() => new Set())
	const fail = useCallback((id: number) => {
		setBroken((old) => (old.has(id) ? old : new Set(old).add(id)))
	}, [])

	if (attachments.length === 0) return null
	const drawable = (file: AttachmentWire) => isPicture(file) && !broken.has(file.id)
	const pictures = attachments.filter(drawable)
	const files = attachments.filter((file) => !drawable(file))

	return (
		<div className="tb-attachments">
			{pictures.length === 0 ? null : (
				<ul
					className="tb-attachments__grid"
					data-count={Math.min(pictures.length, 5)}
					aria-label={t("chat.attachment.images", { count: pictures.length })}
				>
					{pictures.map((file, index) => (
						<li key={file.id}>
							<button
								type="button"
								className="tb-attachments__picture"
								// The box is reserved from the stored size, so nothing below it moves when
								// the thumbnail lands.
								style={{ aspectRatio: `${file.width ?? 1} / ${file.height ?? 1}` }}
								onClick={() => setOpenAt(index)}
							>
								<img
									src={file.thumbUrl}
									alt={altOf(file)}
									loading="lazy"
									decoding="async"
									onError={() => fail(file.id)}
								/>
							</button>
						</li>
					))}
				</ul>
			)}

			{files.length === 0 ? null : (
				<ul className="tb-attachments__files" aria-label={t("chat.attachment.files")}>
					{files.map((file) => (
						<li key={file.id}>
							<AttachmentRow file={file} />
						</li>
					))}
				</ul>
			)}

			{openAt === null ? null : (
				<Lightbox files={pictures} index={openAt} onIndex={setOpenAt} onClose={close} />
			)}
		</div>
	)
}
